import apiFetch from '@scale/frontend-lib/api-fetch';
import { RESUME_SEARCH_DISPLAY_PAGE_SIZE } from './constants';
import { QueryBuilderType, BooleanOperator } from '@scale/scaleui';
import {
  OpenSearchBooleanQuery,
  OpenSearchMatchPhraseQuery,
  ResumeSearchEventType,
  ExportToCsvData,
  SearchQueryData,
  DownloadViewData,
  OpenSearchQuery,
} from '@scale/shared-types';

// bottom up traversal to format query into OpenSearch form
function formatBooleanQuery(
  query: QueryBuilderType | string,
): OpenSearchBooleanQuery | OpenSearchMatchPhraseQuery | undefined {
  if (typeof query === 'string') {
    if (query.length === 0) {
      return undefined;
    }
    if (query[0] === '"' && query[query.length - 1] === '"') {
      return { match_phrase: { text: { query: query.substring(0, query.length - 1), slop: 0 } } };
    }
    return { match_phrase: { text: { query, slop: 3 } } };
  } else {
    let childQueries = [];
    for (const filter of query.filters) {
      childQueries.push(formatBooleanQuery(filter));
    }

    childQueries = childQueries.filter(childQuery => childQuery !== undefined) as (
      | OpenSearchMatchPhraseQuery
      | OpenSearchBooleanQuery
    )[];

    if (query.queryOperator === BooleanOperator.AND) {
      return { bool: { must: childQueries } };
    } else {
      return { bool: { should: childQueries } };
    }
  }
}

function formatQuery(query: QueryBuilderType, ids?: string[]): OpenSearchQuery {
  const booleanQuery = formatBooleanQuery(query) as OpenSearchBooleanQuery;

  if (ids && ids.length > 0) {
    const idQuery = { ids: { values: ids } };
    return { bool: { must: [idQuery, booleanQuery] } };
  }

  return booleanQuery;
}

export async function searchResumes({
  queries,
  ids,
  page = 0,
  includeCount,
  pageSize = RESUME_SEARCH_DISPLAY_PAGE_SIZE,
  includeHighlights = true,
}: {
  queries: QueryBuilderType;
  ids?: string[];
  page?: number;
  includeCount?: boolean;
  pageSize?: number;
  includeHighlights?: boolean;
}) {
  return await apiFetch(null, `corp-api/resume-search/search`, {
    method: 'POST',
    body: {
      queries: formatQuery(queries, ids),
      includeCount,
      includeHighlights,
      pageSize,
      page,
    },
  });
}

export async function fetchAllSearchResults({
  queries,
  ids,
}: {
  queries: QueryBuilderType;
  ids?: string[];
}) {
  return await apiFetch(null, `corp-api/resume-search/scroll-resume-search`, {
    method: 'POST',
    body: {
      queries: formatQuery(queries, ids),
    },
  });
}

export async function emailResumeSearchResults({
  queries,
  ids,
  requesterEmail,
}: {
  queries: QueryBuilderType;
  ids?: string[];
  requesterEmail: string;
}) {
  return await apiFetch(null, `corp-api/resume-search/email-resume-search`, {
    method: 'POST',
    body: {
      queries: formatQuery(queries, ids),
      requesterEmail,
    },
  });
}

export async function logResumeSearchEvent({
  type,
  userId,
  data,
}: {
  type: ResumeSearchEventType;
  userId: string;
  data: ExportToCsvData | SearchQueryData | DownloadViewData;
}) {
  return await apiFetch(null, `corp-api/resume-search/log-resume-search-event`, {
    method: 'POST',
    body: {
      type,
      userId,
      data,
    },
  });
}
